import React, { useCallback, useEffect, useRef, useState } from 'react';
import LockScreen from './LockScreen';
import OfflineHome from './OfflineHome';
import { useAuth } from '../../context/authcontext';
import { TokenService } from '../../services/auth/token.service';
import { refreshSession } from '../../services/auth/cognito.service';
import { userFromIdToken } from '../../services/auth/sessionUser';
import { OfflineSession } from '../../services/offline/offlineSession';
import { Offline, OFFLINE_WIPED_EVENT } from '../../services/offline/offlineServices';
import { isOfflineError } from '../../services/offline/network';
import { useOnlineStatus } from '../../hooks/useOfflineDevice';

/** normal: the regular app; locked: lock screen; offline: unlocked with no connection (read-only). */
export type OfflineGateMode = 'normal' | 'locked' | 'offline';

export interface OfflineGateProps {
  mode: OfflineGateMode;
  onModeChange: (mode: OfflineGateMode) => void;
  /** The regular app is signed in again (online restore after unlock). */
  onAuthenticated: () => void;
  /** Leave for the password sign-in page. */
  onUsePassword: () => void;
  /** Device revoked by an admin: sign out completely. */
  onRevoked: () => void;
  children: React.ReactNode;
}

const HEARTBEAT_MS = 60 * 60 * 1000;

/**
 * Offline Field Mode gate (Phase 16A, design §6.3). Wraps the routed app:
 * - locked → LockScreen; a correct PIN unlocks the encrypted store, then
 *   online: the stored refresh token restores the full session (no password),
 *   offline: the permissions snapshot opens the read-only offline home (within the offline window);
 * - while unlocked and online: keeps the encrypted refresh token current, restarts the offline
 *   window after each online sign-in, and checks the device is still enrolled (heartbeat).
 */
const OfflineGate: React.FC<OfflineGateProps> = ({ mode, onModeChange, onAuthenticated, onUsePassword, onRevoked, children }) => {
  const { user, setUser } = useAuth();
  const online = useOnlineStatus();
  const [reconnecting, setReconnecting] = useState(false);
  const heartbeatAt = useRef(0);

  /** Exchange the stored refresh token for a session. False when offline or Cognito refuses it. */
  const restoreOnline = useCallback(async (): Promise<boolean> => {
    const refreshToken = await OfflineSession.getRefreshToken();
    if (!refreshToken) return false;
    TokenService.saveTokens({ refreshToken });
    if (!(await refreshSession())) return false;
    const restored = userFromIdToken();
    if (!restored) return false;
    setUser(restored);
    onModeChange('normal');
    onAuthenticated();
    return true;
  }, [setUser, onModeChange, onAuthenticated]);

  const enterOffline = useCallback(() => {
    const profile = OfflineSession.getProfile();
    if (!profile || !OfflineSession.offlineAccessAllowed()) {
      OfflineSession.lock();
      onModeChange('locked');
      return;
    }
    setUser(profile.user);
    onModeChange('offline');
  }, [setUser, onModeChange]);

  const handleUnlocked = useCallback(async () => {
    if (navigator.onLine && await restoreOnline().catch(() => false)) return;
    if (navigator.onLine) {
      // Online but the refresh token was refused (expired / revoked): password sign-in
      OfflineSession.lock();
      onUsePassword();
      return;
    }
    enterOffline();
  }, [restoreOnline, enterOffline, onUsePassword]);

  const reconnect = useCallback(async () => {
    setReconnecting(true);
    try {
      if (!(await restoreOnline())) onUsePassword();
    } catch (e) {
      if (!isOfflineError(e)) onUsePassword();
    } finally {
      setReconnecting(false);
    }
  }, [restoreOnline, onUsePassword]);

  // Offline home: connection is back → restore the full app automatically
  useEffect(() => {
    if (mode === 'offline' && online) reconnect();
  }, [mode, online, reconnect]);

  // Revoked device: after the sealed upload and wipe, sign out completely
  useEffect(() => {
    const onWiped = (event: Event) => {
      if ((event as CustomEvent<{ reason: string }>).detail?.reason === 'revoked') onRevoked();
    };
    window.addEventListener(OFFLINE_WIPED_EVENT, onWiped);
    return () => window.removeEventListener(OFFLINE_WIPED_EVENT, onWiped);
  }, [onRevoked]);

  // Keep the encrypted refresh token in step with sign-ins while the store is unlocked
  useEffect(() => TokenService.onChange((authenticated) => {
    const token = TokenService.getRefreshToken();
    if (authenticated && token && OfflineSession.getStore()) OfflineSession.saveRefreshToken(token).catch(() => undefined);
  }), []);

  // Online and unlocked: restart the offline window once permissions are in, and heartbeat
  useEffect(() => {
    const profile = OfflineSession.getProfile();
    if (mode !== 'normal' || !online || !profile || !user?.permissions || user.id !== profile.user.id) return undefined;
    OfflineSession.markOnlineAuth(user).catch(() => undefined);
    const beat = async () => {
      heartbeatAt.current = Date.now();
      // 16C: the sync engine heartbeats with pending counts and visit / shift ids, and on revoke or
      // downgrade syncs first (sealed copies after a revoke), then wipes (OFFLINE_WIPED_EVENT)
      await Offline.engine.checkDevice().catch(() => undefined);
    };
    if (Date.now() - heartbeatAt.current > HEARTBEAT_MS) beat();
    const timer = setInterval(beat, HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [mode, online, user, onRevoked]);

  if (mode === 'locked') {
    return <LockScreen online={online} onUnlocked={handleUnlocked} onUsePassword={() => { onModeChange('normal'); onUsePassword(); }} />;
  }
  if (mode === 'offline' && user) {
    return (
      <OfflineHome
        user={user}
        online={online}
        reconnecting={reconnecting}
        onReconnect={reconnect}
        onLock={() => { OfflineSession.lock(); setUser(null); onModeChange('locked'); }}
      />
    );
  }
  return <>{children}</>;
};

export default OfflineGate;
